/**
 * Album — Vitrine curada do Álbum BB 2026.
 *
 * Exibe:
 *  1. Header: "Meu Álbum BB 2026" + subtítulo + barra de progresso X/15 + 3 stat boxes
 *  2. Faixa de pacote pendente (se stickerAvailable > 0 && !alreadyOpenedToday)
 *  3. Seção "Titulares" (X/7)
 *  4. Seção "Craques + Técnico" (4 craques + divisor + Técnico destacado) (X/5)
 *  5. Seção "Lendárias" (0/3) — 3 cartas largas (Mascote/Estádio/Taça)
 *
 * Quando o usuário seleciona uma figurinha na Coleção e clica em uma
 * posição compatível, solicita confirmação antes de colar (RN-20 + irreversível).
 *
 * Conta total: 15 slots (7 titulares + 4 craques + 1 técnico + 3 lendárias) — §0 spec.
 *
 * RN-18: posição pode ser preenchida apenas uma vez (backend garante)
 * RN-20: behavior da figurinha deve corresponder à posição
 * RN-28: cada colega só pode estar em uma posição (backend garante)
 */

import React, { useState, useRef, useEffect } from 'react';
import { AlbumSlot, CollatedSticker, RepositorySticker, isChallengeCompleted } from 'services/copa.service';
import { StickerCard } from '../StickerCard';
import Humanograma from 'components/Humanograma';
import valeuArt from 'assets/img/campanhas/copa/valeu.png';
import mandouArt from 'assets/img/campanhas/copa/mandou.png';
import uauArt from 'assets/img/campanhas/copa/uau.png';
import valeuLockedArt from 'assets/img/campanhas/copa/block-valeu.png';
import mandouLockedArt from 'assets/img/campanhas/copa/block-mandou.png';
import uauLockedArt from 'assets/img/campanhas/copa/block-uau.png';
import stadiumBg from 'assets/img/campanhas/copa/bg-card.png';
import lockIcon from 'assets/img/campanhas/copa/lock.svg';
import styles from './Album.module.scss';

// ---------------------------------------------------------------------------
// Configurações das lendárias (mapeamento position → cor / arte / regra)
//
// Layout vertical: pílula "LENDÁRIA" → painel de arte (foto do estádio tingida
// + troféu PNG) → título → critério (cadeado + CAPS) → progresso + barra.
// As 3 cartas usam troféus #VALEU / #MANDOUBEM / #UAU (PNGs já recortados).
// Cores (accent/tint/borda) mantidas: teal (VALEU), azul (MANDOUBEM), dourado (UAU).
// ---------------------------------------------------------------------------
interface LegendaryConfig {
  /** Título branco exibido abaixo do painel de arte */
  label: string;
  /** Cor de destaque: pílula, cadeado, critério, progresso e fill da barra */
  accentColor: string;
  /** Gradiente da borda do card (accent vivo no topo → tom escuro embaixo) */
  borderGradient: string;
  /** Fundo escuro do card */
  cardBg: string;
  /** Fundo da pílula "LENDÁRIA" (tokens do Figma — independente do card) */
  pillBg: string;
  /** Cor do texto da pílula "LENDÁRIA" */
  pillText: string;
  /** Cor do overlay (mix-blend color) que tinge a foto do estádio */
  tintColor: string;
  /** Trilho (parte não preenchida) da barra de progresso */
  progressTrack: string;
  /** Troféu (PNG) exibido quando a carta está conquistada */
  art: string;
  /** Disco circular com cadeado (PNG) exibido enquanto a carta está bloqueada */
  lockedArt: string;
  /**
   * Tamanho da arte relativo ao painel (200px no Figma). Os 3 troféus têm o
   * mesmo recorte (~316×304, com folga interna), então usam a mesma escala.
   */
  medalScale: string;
  /**
   * Deslocamento vertical da arte dentro do painel (CSS length, ex: '-6%').
   * Default = centralizado.
   */
  medalOffsetY?: string;
  /** Critério curto em CAPS exibido ao lado do cadeado */
  rule: string;
  progressTotal: number;
}

const LEGENDARY_CONFIG: Record<string, LegendaryConfig> = {
  lendaria_mascote: {
    label: 'Troféu #VALEU',
    accentColor: '#7cffeb',
    borderGradient:
      'linear-gradient(180deg, #7cffeb 0%, #2a1618 25%, #c4fff6 50%, #2a1618 75%, #7cffeb 100%)',
    cardBg: '#2a1618',
    pillBg: '#674548',
    pillText: '#7cffeb',
    tintColor: '#276158',
    progressTrack: '#674548',
    art: valeuArt,
    lockedArt: valeuLockedArt,
    medalScale: '85%',
    rule: '5 comportamentos diferentes',
    progressTotal: 5,
  },
  lendaria_estadio: {
    label: 'Troféu #MANDOUBEM',
    // accentColor clareado de #3d5aff → #6f90ff: como texto pequeno (9–10px) sobre
    // o cardBg #002542, #3d5aff dava ~3:1 (reprova AA 4.5:1); #6f90ff dá ~5.3:1.
    accentColor: '#6f90ff',
    borderGradient:
      'linear-gradient(180deg, #6f90ff 0%, #002542 25%, #9da3c2 50%, #002542 75%, #6f90ff 100%)',
    cardBg: '#002542',
    pillBg: '#d2d8ff',
    // pillText escurecido de #3d5aff → #2f43c4: como texto sobre o pillBg claro
    // #d2d8ff, #3d5aff dava ~3.7:1; #2f43c4 dá ~5.4:1 (AA).
    pillText: '#2f43c4',
    tintColor: '#002542',
    progressTrack: '#b3beff',
    art: mandouArt,
    lockedArt: mandouLockedArt,
    medalScale: '85%',
    rule: '8 dias de pacote aberto',
    progressTotal: 8,
  },
  lendaria_taca: {
    label: 'Troféu #UAU',
    accentColor: '#e0b93a',
    borderGradient:
      'linear-gradient(180deg, #fcfc30 0%, #816507 25%, #ffff9d 50%, #816507 75%, #fcfc30 100%)',
    cardBg: '#2e2a0a',
    pillBg: '#ffffb3',
    pillText: '#816507',
    tintColor: '#7a6a2a',
    progressTrack: '#6b5a2a',
    art: uauArt,
    lockedArt: uauLockedArt,
    medalScale: '85%',
    rule: 'Complete o time base e as lendárias anteriores',
    // RN-06: 7 Titulares colados + Mascote + Estádio = 9 (Craques/Técnico não entram).
    progressTotal: 9,
  },
};

// Escala do disco de bloqueio (PNG circular) dentro do painel de arte.
// O disco é sólido/cheio, então "lê" maior que o halo verde do troféu (que é só
// um anel atrás do troféu). ~52% deixa o disco com o tamanho aparente do halo.
const LOCKED_MEDAL_SCALE = '52%';

// Deslocamento vertical do disco de bloqueio (desce um pouco em relação ao centro
// do painel, alinhando melhor com o halo do troféu conquistado).
const LOCKED_MEDAL_OFFSET_Y = '8px';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface AlbumProps {
  /** Slots não-lendários (titulares + craques + técnico) */
  slots: AlbumSlot[];
  /** Slots lendários */
  legendarySlots: AlbumSlot[];
  /** Desafios da Copa (filtrados extra.type === 'copa') para lendárias */
  challenges: any[];
  /** Total de cartas COLADAS (titulares+craques+técnico) — base do progresso da Taça */
  completedCount: number;
  /** Total conquistado (coladas + lendárias) — usado no "X/15" e nas stats */
  conqueredCount?: number;
  /** Lendárias conquistadas (0–3) */
  legendaryCount?: number;
  /** Dias distintos de Pacote do Dia abertos (Estádio, meta 8) */
  estadioDays?: number;
  /** Comportamentos distintos enviados pelo jogador (Mascote, meta 5) */
  mascoteBehaviors?: number;
  /** Pacotes disponíveis para abrir */
  stickerAvailable?: number;
  /** Envelopes abríveis agora (daily + rodada dupla + bônus) */
  pendingCount?: number;
  /** Jogador já abriu hoje */
  alreadyOpenedToday?: boolean;
  /** Navega para aba Pacote */
  onOpenPack?: () => void;
  /** Repositório completo (Coleção) — usado pelo picker de cada slot */
  repository?: RepositorySticker[];
  /** Sticker selecionado no Repositório para colar */
  selectedSticker?: RepositorySticker | null;
  onCollate: (sticker: RepositorySticker, position: string) => Promise<void>;
  /** Troca a figurinha de uma posição preenchida por outra da Coleção (RF-08/RN-31) */
  onSubstitute?: (current: CollatedSticker, replacement: RepositorySticker) => Promise<void>;
  onClearSelection: () => void;
  /** Clicar numa figurinha colada abre o detalhe do reconhecimento (RN-30) */
  onViewRecognition?: (recognitionId: string) => void;
  /** Álbum congelado (campanha encerrada, RE-07): sem colar/trocar/pacote pendente */
  readOnly?: boolean;
}

// Rótulos de posição e comportamento (para o título do picker)
const BEHAVIOR_LABELS: Record<string, string> = {
  foco: 'Foco',
  colaboracao: 'Colaboração',
  inovacao: 'Inovação',
  resolutividade: 'Protagonismo',
  lideranca: 'Liderança',
};

function positionLabel(position: string): string {
  if (position === 'goleiro') return 'Goleiro';
  if (position.startsWith('defensor')) return 'Lateral';
  if (position.startsWith('meio')) return 'Meio-campo';
  if (position.startsWith('atacante')) return 'Atacante';
  if (position.startsWith('craque')) return 'Craque';
  if (position === 'tecnico') return 'Técnico';
  return position;
}

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------
const TOTAL_SLOTS = 15;

// Posições das 3 Lendárias (challenge-based, não vêm de config.positions)
const LEGENDARY_POSITIONS = ['lendaria_mascote', 'lendaria_estadio', 'lendaria_taca'];

// Ordem das seções não-lendárias
const SECTION_ORDER = ['titular', 'craque'] as const;

const SECTION_LABELS: Record<string, string> = {
  titular: 'Time Base',
  craque: 'Craques + Técnico',
};

const SECTION_SUBLABELS: Record<string, string> = {
  titular: 'Comportamentos do dia a dia',
  craque: 'Um nível acima',
};

const SECTION_ACCENT: Record<string, string> = {
  titular: '#7cffeb',
  craque: '#fbd34d',
};

const SECTION_DIVIDER: Record<string, string> = {
  titular: '#062b48',
  craque: '#3a2f10',
};

const SECTION_COUNT_COLOR: Record<string, string> = {
  titular: '#7cffeb',
  craque: '#fbd34d',
};

// ---------------------------------------------------------------------------
// Helper — mini foil pack SVG inline
// ---------------------------------------------------------------------------
function MiniPack() {
  return (
    <div className={styles.miniPack} aria-hidden="true">
      <div className={styles.miniPackRibbing} />
      <div className={styles.miniPackSeal} />
      <svg
        viewBox="0 0 24 24"
        width="13"
        height="13"
        fill="#fff"
        className={styles.miniPackStar}
      >
        <path d="M12 2l2.4 6.9H22l-6 4.4 2.3 7-6.3-4.6L5.7 20.3 8 13.3l-6-4.4h7.6z" />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// useFocusTrap — move foco para o dialog ao abrir, prende Tab/Shift+Tab,
// fecha com Escape e restaura o foco original ao fechar.
// ---------------------------------------------------------------------------
function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  onClose: () => void,
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const container = containerRef.current;
    const previousFocus = document.activeElement as HTMLElement | null;

    const getFocusable = () =>
      Array.from(
        container.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );

    // Aguarda o DOM pintar antes de focar (picker pode ter lista dinâmica)
    const rafId = requestAnimationFrame(() => {
      getFocusable()[0]?.focus();
    });

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // stopPropagation impede que o Esc chegue ao react-modal do CopaModal
        // (shouldCloseOnEsc) e feche o álbum inteiro junto com o picker/confirmação.
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusable = getFocusable();
      if (focusable.length === 0) { e.preventDefault(); e.stopPropagation(); return; }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !container.contains(document.activeElement)) {
          e.preventDefault();
          e.stopPropagation();
          last?.focus();
        }
      } else {
        if (document.activeElement === last || !container.contains(document.activeElement)) {
          e.preventDefault();
          e.stopPropagation();
          first?.focus();
        }
      }
    }

    // Redireciona foco de volta quando escapa por clique ou programaticamente
    function handleFocusIn(e: FocusEvent) {
      if (!container.contains(e.target as Node)) {
        getFocusable()[0]?.focus();
      }
    }

    // capture:true = dispara antes do handler de bubbling do react-modal (CopaModal),
    // garantindo prioridade ao picker/confirmação aninhados.
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      previousFocus?.focus();
    };
  }, [isOpen, containerRef]);
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
export function Album({
  slots,
  legendarySlots,
  challenges,
  completedCount,
  conqueredCount,
  legendaryCount,
  estadioDays = 0,
  mascoteBehaviors = 0,
  stickerAvailable = 0,
  pendingCount = 0,
  alreadyOpenedToday = false,
  onOpenPack,
  repository = [],
  selectedSticker,
  onCollate,
  onSubstitute,
  onClearSelection,
  onViewRecognition,
  readOnly = false,
}: AlbumProps) {
  // Slot cujo picker está aberto (escolher figurinha para colar)
  const [pickerSlot, setPickerSlot] = useState<AlbumSlot | null>(null);
  // Confirmação final: figurinha escolhida + slot
  const [confirming, setConfirming] = useState<{ slot: AlbumSlot; sticker: RepositorySticker } | null>(null);
  const [isCollating, setIsCollating] = useState(false);
  // Troca (RF-08/RN-31): slot preenchido cujo seletor de troca está aberto + confirmação
  const [swapSlot, setSwapSlot] = useState<AlbumSlot | null>(null);
  const [swapConfirming, setSwapConfirming] = useState<{ slot: AlbumSlot; sticker: RepositorySticker } | null>(null);
  const [isSwapping, setIsSwapping] = useState(false);

  const pickerRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  const swapPickerRef = useRef<HTMLDivElement>(null);
  const swapConfirmRef = useRef<HTMLDivElement>(null);
  useFocusTrap(pickerRef, !!pickerSlot, () => setPickerSlot(null));
  useFocusTrap(confirmRef, !!confirming && !isCollating, () => setConfirming(null));
  useFocusTrap(swapPickerRef, !!swapSlot, () => setSwapSlot(null));
  useFocusTrap(swapConfirmRef, !!swapConfirming && !isSwapping, () => setSwapConfirming(null));

  // Figurinhas elegíveis para um slot: mesmo tipo + behavior, ainda colável
  function eligibleFor(slot: AlbumSlot): RepositorySticker[] {
    return repository.filter(
      s => s.type === slot.tipo && s.behavior === slot.behavior && !s.collected && !s.colleagueLocked,
    );
  }

  // ---------------------------------------------------------------------------
  // Agrupamento: técnico vai junto com craque (bloco "Craques + Técnico")
  // ---------------------------------------------------------------------------
  const sections = slots.reduce<Record<string, AlbumSlot[]>>((acc, slot) => {
    const key = slot.tipo === 'tecnico' ? 'craque' : slot.tipo;
    if (!acc[key]) acc[key] = [];
    acc[key].push(slot);
    return acc;
  }, {});

  // Técnico é sempre o último do bloco craque
  if (sections.craque) {
    sections.craque.sort((a, b) => {
      if (a.tipo === 'tecnico') return 1;
      if (b.tipo === 'tecnico') return -1;
      return 0;
    });
  }

  // Stat boxes — as Lendárias NÃO vêm de config.positions (são challenge-based).
  // Usa legendaryCount/conqueredCount do hook (fonte única); fallback ao cálculo local.
  const legendaryCompleted =
    legendaryCount ?? LEGENDARY_POSITIONS.filter(pos => getLegendaryProgress(pos).done).length;
  // "X/15" e "Conquistadas" = coladas + lendárias conquistadas.
  const conquered = conqueredCount ?? completedCount + legendaryCompleted;
  const remaining = TOTAL_SLOTS - conquered;
  const progressPct = Math.min(100, (conquered / TOTAL_SLOTS) * 100);

  // Faixa de pacote pendente: há envelopes a abrir (daily/rodada/bônus) e pool disponível.
  // Álbum congelado (readOnly) não tem mais pacotes — a faixa nunca aparece.
  const showPendingPack = !readOnly && pendingCount > 0 && stickerAvailable > 0;

  // Slot destacado quando há uma figurinha pré-selecionada na Coleção e é compatível
  function canCollateHere(slot: AlbumSlot): boolean {
    if (!selectedSticker) return false;
    if (slot.sticker) return false;
    return slot.behavior === selectedSticker.behavior && slot.tipo === selectedSticker.type;
  }

  // Clique num slot VAZIO: se há figurinha pré-selecionada compatível, confirma;
  // senão, abre o picker com as figurinhas elegíveis para a posição.
  function handleSlotClick(slot: AlbumSlot) {
    if (readOnly) return; // álbum congelado (RE-07) — sem colar
    if (slot.sticker) return; // já preenchido
    if (selectedSticker && canCollateHere(slot)) {
      setConfirming({ slot, sticker: selectedSticker });
      return;
    }
    setPickerSlot(slot);
  }

  // Escolheu uma figurinha no picker → vai para a confirmação
  function handlePick(slot: AlbumSlot, sticker: RepositorySticker) {
    setPickerSlot(null);
    setConfirming({ slot, sticker });
  }

  async function handleConfirm() {
    if (!confirming) return;
    setIsCollating(true);
    try {
      await onCollate(confirming.sticker, confirming.slot.position);
      setConfirming(null);
      onClearSelection();
    } finally {
      setIsCollating(false);
    }
  }

  // Troca (RF-08/RN-31): mesmos candidatos de uma colagem (mesmo tipo+behavior,
  // não colados / colega não travado) — a carta atual já é `collected`, então sai.
  // Escolheu no seletor de troca → confirmação → onSubstitute(carta atual, nova).
  function handleSwapPick(slot: AlbumSlot, sticker: RepositorySticker) {
    setSwapSlot(null);
    setSwapConfirming({ slot, sticker });
  }

  async function handleSwapConfirm() {
    if (!swapConfirming || !onSubstitute || !swapConfirming.slot.sticker) return;
    setIsSwapping(true);
    try {
      await onSubstitute(swapConfirming.slot.sticker, swapConfirming.sticker);
      setSwapConfirming(null);
    } finally {
      setIsSwapping(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Lendária — busca progresso no challenge matching
  // ---------------------------------------------------------------------------
  // Progresso PARCIAL real de cada lendária (handoff #24):
  //  Mascote = comportamentos distintos enviados (meta 5);
  //  Estádio = dias distintos de pacote (meta 8);
  //  Taça    = critério COMPOSTO (RN-06): 7 Titulares colados + Mascote + Estádio
  //            (meta 9). Craques/Técnico NÃO entram (decisão cliente 24/jun).
  // A conclusão (passo do desafio FyC6agj) também enche a barra.
  function getLegendaryProgress(position: string): { current: number; total: number; done: boolean } {
    const cfg = LEGENDARY_CONFIG[position];
    if (!cfg) return { current: 0, total: 1, done: false };

    const ch = challenges.find(
      (c: any) => c._id === 'FyC6agj' || c.extra?.order === 3 || c.extra?.total === 3,
    );
    // Bloco lendário inteiro concluído: o `progress` some e vira achievement
    // (campo `completed`). Sem `progress` + `completed` ⇒ as 3 conquistadas.
    const blockDone = !!ch && !ch.progress && isChallengeCompleted(ch);

    // Uma rule do desafio Lendárias está cumprida — parcial via `progress`, ou
    // bloco inteiro já concluído (rule 0=mascote, 1=estádio, 2=taça).
    const ruleDone = (idx: number): boolean => {
      if (blockDone) return true;
      const r = ch?.progress?.rules?.[idx];
      return (
        !!r?.completed ||
        (typeof r?.times_completed === 'number' && r.times_completed >= (r.times_required ?? 1))
      );
    };

    const ruleIndex =
      position === 'lendaria_mascote' ? 0 : position === 'lendaria_estadio' ? 1 : 2;
    const done = ruleDone(ruleIndex);

    const total = cfg.progressTotal; // 5 / 8 / 9
    let raw: number;
    if (position === 'lendaria_mascote') {
      raw = mascoteBehaviors;
    } else if (position === 'lendaria_estadio') {
      raw = estadioDays;
    } else {
      // Taça (RN-06): 7 Titulares colados + Mascote + Estádio.
      const titularesColados = slots.filter(s => s.tipo === 'titular' && s.sticker).length;
      raw = Math.min(titularesColados, 7) + (ruleDone(0) ? 1 : 0) + (ruleDone(1) ? 1 : 0);
    }
    const current = done ? total : Math.min(raw, total);
    return { current, total, done };
  }

  return (
    <div className={styles.wrapper}>
      {/* ═══════════════════════════════════════════════════════════════
          HEADER: título + progresso X/15 + 3 stat boxes
      ═══════════════════════════════════════════════════════════════ */}
      <div className={styles.albumHeader}>
        <div className={styles.albumHeaderBg} />
        <div className={styles.albumHeaderInner}>
          {/* Left: título */}
          <div className={styles.albumHeaderLeft}>
            <div className={styles.campaignPill} aria-hidden="true">
              <span className={styles.campaignPillDot} />
              {readOnly ? 'Campanha encerrada' : 'Campanha ativa · 1S2026'}
            </div>
            <h2 className={styles.albumTitle}>Meu Álbum BB 2026</h2>
            <p className={styles.albumSubtitle}>
              {readOnly
                ? 'Veja sua escalação como ela ficou ao final da campanha.'
                : 'Você reconheceu e foi reconhecido — veja sua escalação tomar forma.'}
            </p>
          </div>
          {/* Right: progresso */}
          {/* A11Y: a seção de progresso não tem controle interativo, então o Tab a
              pularia. Tornamos a região focável (tabIndex=0 + role="group") com um
              rótulo que resume tudo — assim teclado/leitor de tela param aqui e
              ouvem o progresso completo numa única parada. */}
          <div
            className={styles.albumHeaderRight}
            tabIndex={0}
            role="group"
            aria-label={`Progresso do álbum: ${conquered} de 15 cartas. ${conquered} conquistadas, ${remaining} faltam, ${legendaryCompleted} de 3 lendárias.`}
          >
            <div className={styles.progressHeader}>
              <span className={styles.progressLabel}>Progresso geral</span>
              <span className={styles.progressCount}>
                <b style={{ color: '#fbfd00', fontSize: 15 }}>{conquered}</b>/15 cartas
              </span>
            </div>
            <div className={styles.progressTrack} role="progressbar" aria-valuenow={conquered} aria-valuemin={0} aria-valuemax={TOTAL_SLOTS} aria-label={`${conquered} de 15 cartas`}>
              <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
            </div>
            {/* Stat boxes — cada box tem nome acessível único (role="img") para o
                leitor não ler valor e rótulo grudados ("6Conquistadas 9Faltam"). */}
            <div className={styles.statBoxes}>
              <div className={styles.statBox} role="img" aria-label={`${conquered} conquistadas`}>
                <div className={styles.statValue} style={{ color: '#7cffeb' }} aria-hidden="true">{conquered}</div>
                <div className={styles.statLabel} aria-hidden="true">Conquistadas</div>
              </div>
              <div className={styles.statBox} role="img" aria-label={`${remaining} faltam`}>
                <div className={styles.statValue} style={{ color: '#fbfd00' }} aria-hidden="true">{remaining}</div>
                <div className={styles.statLabel} aria-hidden="true">Faltam</div>
              </div>
              <div className={styles.statBox} role="img" aria-label={`${legendaryCompleted} de 3 lendárias`}>
                <div className={styles.statValue} aria-hidden="true">
                  {legendaryCompleted}
                  <span style={{ fontSize: 11, color: '#a9bcd6' }}>/3</span>
                </div>
                <div className={styles.statLabel} aria-hidden="true">Lendárias</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          FAIXA DE PACOTE PENDENTE
      ═══════════════════════════════════════════════════════════════ */}
      {showPendingPack && (
        <div className={styles.pendingStrip} role="status">
          <MiniPack />
          <div className={styles.pendingText}>
            <div className={styles.pendingTitle}>
              {pendingCount > 1
                ? `Você tem ${pendingCount} envelopes para abrir`
                : 'Você tem 1 envelope para abrir'}
            </div>
            <div className={styles.pendingSub}>
              Reconheça colegas para ganhar envelopes bônus.
            </div>
          </div>
          {onOpenPack && (
            <button
              className={styles.pendingBtn}
              onClick={onOpenPack}
              type="button"
              aria-label="Abrir envelope do dia"
            >
              Abrir envelope
            </button>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          Hint de sticker selecionado
      ═══════════════════════════════════════════════════════════════ */}
      {selectedSticker && (
        <div className={styles.selectionHint} role="status" aria-live="polite">
          <span>
            Figurinha selecionada: <strong>{selectedSticker.colleagueName ?? selectedSticker.colleagueId}</strong>.
            Clique em uma posição compatível para colar.
          </span>
          <button
            className={styles.cancelBtn}
            onClick={onClearSelection}
            aria-label="Cancelar seleção de figurinha"
            type="button"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SEÇÕES: Titulares + Craques+Técnico
      ═══════════════════════════════════════════════════════════════ */}
      <div className={styles.sectionsArea}>
        {SECTION_ORDER.map(type => {
          const group = sections[type];
          if (!group?.length) return null;
          const filledCount = group.filter(s => s.sticker).length;
          const total = group.length;
          const accentColor = SECTION_ACCENT[type];
          const dividerColor = SECTION_DIVIDER[type];
          const countColor = SECTION_COUNT_COLOR[type];

          return (
            <section
              key={type}
              className={styles.albumSection}
              aria-label={`${SECTION_LABELS[type]}: ${filledCount}/${total}`}
            >
              {/* Section header */}
              <div className={styles.sectionHeader}>
                <span className={styles.sectionDot} style={{ background: accentColor }} />
                <h3 className={styles.sectionTitle}>{SECTION_LABELS[type]}</h3>
                <span className={styles.sectionSub}>
                  {SECTION_SUBLABELS[type]} ·{' '}
                  <b style={{ color: countColor }}>{filledCount}</b>/{total}
                </span>
                <div className={styles.sectionDivider} style={{ background: dividerColor }} />
              </div>

              {/* Cards grid */}
              <div className={styles.cardsRow} role="list" aria-label={SECTION_LABELS[type]}>
                {group.map(slot => {
                  const isTarget = canCollateHere(slot);
                  // Técnico mantém o destaque visual, mas agora é colável (RN-05/ADR-17).
                  const isTecnico = slot.tipo === 'tecnico';

                  return (
                    <div
                      key={slot.position}
                      role="listitem"
                      className={`${styles.cardWrap} ${isTarget ? styles.targetSlot : ''} ${isTecnico ? styles.cardWrapTecnico : ''}`}
                    >
                      {slot.sticker ? (
                        <>
                          <StickerCard
                            sticker={{
                              recognitionId: slot.sticker.recognitionId,
                              type: slot.sticker.tipo,
                              behavior: slot.sticker.behavior,
                              colleagueId: slot.sticker.colleagueId,
                              colleagueName: slot.sticker.colleagueName,
                              image: slot.sticker.image,
                              avatar: slot.sticker.avatar,
                              avatarType: slot.sticker.avatarType,
                            }}
                            position={slot.position}
                            hideName
                            onClick={
                              onViewRecognition && slot.sticker.recognitionId
                                ? () => onViewRecognition(slot.sticker!.recognitionId)
                                : undefined
                            }
                          />
                          {/* Linha irmã da carta (sem <button>/<a> aninhado — toque na carta
                              abre o reconhecimento, RN-30): nome+matrícula linkam para o
                              Humanograma, e o ícone de ALTERAR (RF-08/RN-31) fica ao lado,
                              só quando há alternativa na Coleção (EC-13). */}
                          <div className={styles.nameActionsRow}>
                            <div className={styles.nameLinkWrap}>
                              <Humanograma
                                data={{
                                  _id: slot.sticker.colleagueId,
                                  nome: slot.sticker.colleagueName ?? slot.sticker.colleagueId,
                                  funcao: slot.sticker.funcao,
                                  prefixo: slot.sticker.prefixo,
                                  nome_dependencia: slot.sticker.nome_dependencia,
                                }}
                              >
                                <span className={styles.nameLinkBlock}>
                                  <span className={styles.nameLink}>{slot.sticker.colleagueName ?? slot.sticker.colleagueId}</span>
                                  <span className={styles.nameLinkId}>({slot.sticker.colleagueId})</span>
                                </span>
                              </Humanograma>
                            </div>
                            {!readOnly && onSubstitute && eligibleFor(slot).length > 0 && (
                              <button
                                type="button"
                                className={styles.swapBtn}
                                // Cor segue a moldura: titular=teal, craque/técnico=dourado.
                                style={{ ['--swap-accent' as string]: slot.tipo === 'titular' ? '#7cffeb' : '#fbd34d' }}
                                onClick={() => setSwapSlot(slot)}
                                aria-label={`Alterar figurinha de ${positionLabel(slot.position)}`}
                                title="Alterar figurinha"
                              >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <path d="M4 8h13.5M14.5 4.5 18 8l-3.5 3.5" />
                                  <path d="M20 16H6.5M9.5 12.5 6 16l3.5 3.5" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </>
                      ) : (
                        <StickerCard
                          sticker={{
                            recognitionId: '',
                            type: slot.tipo,
                            behavior: slot.behavior,
                            colleagueId: '',
                          }}
                          isEmpty
                          hasCollatable={!readOnly && eligibleFor(slot).length > 0}
                          position={slot.position}
                          onClick={() => handleSlotClick(slot)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        {/* ═══════════════════════════════════════════════════════════
            SEÇÃO: LENDÁRIAS — cartas largas horizontais
        ═══════════════════════════════════════════════════════════ */}
        <section
          className={styles.albumSection}
          aria-label={`Lendárias: ${legendaryCompleted}/3`}
        >
          {/* Section header */}
          <div className={styles.sectionHeader}>
            <span className={styles.sectionDot} style={{ background: '#dca09b' }} />
            <h3 className={styles.sectionTitle}>Lendárias</h3>
            <span className={styles.sectionSub}>
              Só por conquistas especiais ·{' '}
              <b style={{ color: '#dca09b' }}>{legendaryCompleted}</b>/3
            </span>
            <div className={styles.sectionDivider} style={{ background: '#2a1d44' }} />
          </div>

          {/* Lendárias wide cards — cada card é informativo (sem ação), então
              vira uma unidade focável e rotulada (role="img" + tabIndex=0), igual
              aos stat boxes: o Tab para em cada uma e o leitor lê um resumo limpo
              em vez do texto grudado. O agrupamento "Lendárias" já vem da <section>. */}
          <div className={styles.legendaryCards}>
            {(legendarySlots.length > 0
              ? legendarySlots
              : [
                  { position: 'lendaria_mascote', tipo: 'lendaria' as const, behavior: 'lideranca', sticker: undefined },
                  { position: 'lendaria_estadio', tipo: 'lendaria' as const, behavior: 'lideranca', sticker: undefined },
                  { position: 'lendaria_taca', tipo: 'lendaria' as const, behavior: 'lideranca', sticker: undefined },
                ]
            ).map(slot => {
              const cfg = LEGENDARY_CONFIG[slot.position] ?? LEGENDARY_CONFIG.lendaria_mascote;
              const { current, total, done } = getLegendaryProgress(slot.position);
              const pct = Math.min(100, Math.round((current / total) * 100));
              const isConquistada = done;
              // Conquistada (decisão #2): sem cadeado, barra 100%, "conquistada".
              const progressText = isConquistada ? 'conquistada' : `${current}/${total}`;

              return (
                <div
                  key={slot.position}
                  role="img"
                  tabIndex={0}
                  aria-label={`${cfg.label}: ${
                    isConquistada ? 'conquistada' : `bloqueada, progresso ${current} de ${total}`
                  }. Critério: ${cfg.rule}.`}
                  className={styles.legendaryCard}
                  style={{
                    // Borda em gradiente: preenche o interior (padding-box) com o cardBg
                    // e a borda (border-box) com o gradiente accent→escuro.
                    background: `linear-gradient(${cfg.cardBg}, ${cfg.cardBg}) padding-box, ${cfg.borderGradient} border-box`,
                  }}
                >
                  {/* Painel de arte: foto do estádio tingida + medalha PNG.
                      Bloqueada não exibe a foto — só o disco sobre o fundo do card. */}
                  <div className={styles.legendaryArt}>
                    {isConquistada && (
                      <>
                        <img
                          className={styles.legendaryArtBg}
                          src={stadiumBg}
                          alt=""
                          aria-hidden="true"
                        />
                        <div
                          className={styles.legendaryArtTint}
                          style={{ background: cfg.tintColor }}
                        />
                      </>
                    )}
                    <img
                      className={`${styles.legendaryMedal} ${isConquistada ? styles.legendaryMedalConquered : ''}`}
                      src={isConquistada ? cfg.art : cfg.lockedArt}
                      alt=""
                      aria-hidden="true"
                      style={{
                        width: isConquistada ? cfg.medalScale : LOCKED_MEDAL_SCALE,
                        // Bloqueada: painel não-quadrado → height auto mantém o
                        // disco circular dimensionado pela largura (sem cortar).
                        height: isConquistada ? cfg.medalScale : 'auto',
                        ['--medal-offset-y' as string]: isConquistada
                          ? cfg.medalOffsetY ?? '0px'
                          : LOCKED_MEDAL_OFFSET_Y,
                      }}
                    />

                    {/* Pílula "LENDÁRIA". Conquistada: mantém o visual antigo
                        (fundo do card + accent). Bloqueada: tokens próprios do
                        Figma (fundo claro/escuro + texto), sem borda. */}
                    <span
                      className={styles.legendaryPill}
                      style={
                        isConquistada
                          ? { background: cfg.cardBg, color: cfg.accentColor }
                          : { background: cfg.pillBg, color: cfg.pillText }
                      }
                    >
                      Lendária
                    </span>
                  </div>

                  {/* Título */}
                  <h4 className={styles.legendaryLabel}>{cfg.label}</h4>

                  {/* Critério: texto curto em CAPS centralizado */}
                  <div className={styles.legendaryCriterion}>
                    <span className={styles.legendaryRule} style={{ color: cfg.accentColor }}>
                      {cfg.rule}
                    </span>
                  </div>

                  {/* Linha de progresso */}
                  <div className={styles.legendaryProgressHeader}>
                    <span style={{ color: cfg.accentColor }}>Progresso</span>
                    <span style={{ color: cfg.accentColor }}>{progressText}</span>
                  </div>
                  <div
                    className={styles.legendaryProgressTrack}
                    style={{ background: cfg.progressTrack }}
                  >
                    <div
                      className={styles.legendaryProgressFill}
                      style={{ width: `${pct}%`, background: cfg.accentColor }}
                    />
                  </div>

                  {/* Rodapé "Bloqueada" — só enquanto a carta não foi conquistada */}
                  {!isConquistada && (
                    <div className={styles.legendaryFooter}>
                      <span
                        className={styles.legendaryFooterLock}
                        style={{
                          WebkitMaskImage: `url(${lockIcon})`,
                          maskImage: `url(${lockIcon})`,
                        }}
                        aria-hidden="true"
                      />
                      Bloqueada
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          Picker: escolher figurinha para um slot vazio
      ═══════════════════════════════════════════════════════════════ */}
      {pickerSlot && (
        <div
          ref={pickerRef}
          className={styles.pickerOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={`Escolher figurinha para ${positionLabel(pickerSlot.position)}`}
        >
          <div className={styles.pickerBox}>
            <div className={styles.pickerHeader}>
              <div>
                <h3 className={styles.pickerTitle}>
                  {positionLabel(pickerSlot.position)} · {BEHAVIOR_LABELS[pickerSlot.behavior] ?? pickerSlot.behavior}
                </h3>
                <p className={styles.pickerSub}>Escolha quem entra nesta posição.</p>
              </div>
              <button
                className={styles.pickerClose}
                onClick={() => setPickerSlot(null)}
                aria-label="Fechar"
                type="button"
              >
                ✕
              </button>
            </div>

            {(() => {
              const eligible = eligibleFor(pickerSlot);
              if (eligible.length === 0) {
                return (
                  <div className={styles.pickerEmpty}>
                    <p>Você ainda não tem figurinhas para esta posição.</p>
                    <p className={styles.pickerEmptyHint}>Abra mais Pacotes do Dia para conseguir.</p>
                  </div>
                );
              }
              return (
                <div className={styles.pickerGrid} role="list">
                  {eligible.map(s => (
                    <div key={s.recognitionId} role="listitem" className={styles.pickerCard}>
                      <StickerCard
                        sticker={{
                          recognitionId: s.recognitionId,
                          type: s.type,
                          behavior: s.behavior,
                          colleagueId: s.colleagueId,
                          colleagueName: s.colleagueName,
                          image: s.image,
                          avatar: s.avatar,
                          avatarType: s.avatarType,
                        }}
                      />
                      <button
                        className={styles.pickerAddBtn}
                        onClick={() => handlePick(pickerSlot, s)}
                        type="button"
                        aria-label={`Colar ${s.colleagueName ?? s.colleagueId} em ${positionLabel(pickerSlot.position)}`}
                      >
                        + Adicionar
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          Modal de confirmação de colagem
      ═══════════════════════════════════════════════════════════════ */}
      {confirming && (
        <div
          ref={confirmRef}
          className={styles.confirmOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Confirmar a escalação"
        >
          <div className={styles.confirmBox}>
            <p className={styles.confirmTitle}>
              Confirmar a escalação?
            </p>
            <p className={styles.confirmSub}>
              Você está escalando{' '}
              <strong>{confirming.sticker.colleagueName ?? confirming.sticker.colleagueId}</strong>{' '}
              na posição de <strong>{positionLabel(confirming.slot.position)} · {BEHAVIOR_LABELS[confirming.slot.behavior] ?? confirming.slot.behavior}</strong>.
              Até o fim da campanha você poderá alterar essa figurinha.
            </p>
            <div className={styles.confirmActions}>
              <button
                className={styles.confirmBtn}
                onClick={handleConfirm}
                disabled={isCollating}
                aria-label="Confirmar escalação"
                type="button"
              >
                {isCollating ? 'Escalando...' : 'Confirmar'}
              </button>
              <button
                className={styles.cancelConfirmBtn}
                onClick={() => setConfirming(null)}
                disabled={isCollating}
                aria-label="Revisar"
                type="button"
              >
                Revisar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          Seletor de TROCA (RF-08/RN-31): escolher quem entra no lugar
      ═══════════════════════════════════════════════════════════════ */}
      {swapSlot && swapSlot.sticker && (
        <div
          ref={swapPickerRef}
          className={styles.pickerOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={`Alterar figurinha de ${positionLabel(swapSlot.position)}`}
        >
          <div className={styles.pickerBox}>
            <div className={styles.pickerHeader}>
              <div>
                <h3 className={styles.pickerTitle}>
                  Alterar · {positionLabel(swapSlot.position)} · {BEHAVIOR_LABELS[swapSlot.behavior] ?? swapSlot.behavior}
                </h3>
                <p className={styles.pickerSub}>
                  No lugar de <strong>{swapSlot.sticker.colleagueName ?? swapSlot.sticker.colleagueId}</strong>.
                  A carta atual volta para a Coleção.
                </p>
              </div>
              <button
                className={styles.pickerClose}
                onClick={() => setSwapSlot(null)}
                aria-label="Fechar"
                type="button"
              >
                ✕
              </button>
            </div>

            <div className={styles.pickerGrid} role="list">
              {eligibleFor(swapSlot).map(s => (
                <div key={s.recognitionId} role="listitem" className={styles.pickerCard}>
                  <StickerCard
                    sticker={{
                      recognitionId: s.recognitionId,
                      type: s.type,
                      behavior: s.behavior,
                      colleagueId: s.colleagueId,
                      colleagueName: s.colleagueName,
                      image: s.image,
                      avatar: s.avatar,
                      avatarType: s.avatarType,
                    }}
                  />
                  <button
                    className={styles.pickerAddBtn}
                    onClick={() => handleSwapPick(swapSlot, s)}
                    type="button"
                    aria-label={`Alterar para ${s.colleagueName ?? s.colleagueId} em ${positionLabel(swapSlot.position)}`}
                  >
                    Alterar
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          Confirmação de TROCA
      ═══════════════════════════════════════════════════════════════ */}
      {swapConfirming && swapConfirming.slot.sticker && (
        <div
          ref={swapConfirmRef}
          className={styles.confirmOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Confirmar alteração de figurinha"
        >
          <div className={styles.confirmBox}>
            <p className={styles.confirmTitle}>Alterar a figurinha desta posição?</p>
            <p className={styles.confirmSub}>
              <strong>{swapConfirming.slot.sticker.colleagueName ?? swapConfirming.slot.sticker.colleagueId}</strong>{' '}
              sai e <strong>{swapConfirming.sticker.colleagueName ?? swapConfirming.sticker.colleagueId}</strong>{' '}
              entra em <strong>{positionLabel(swapConfirming.slot.position)} · {BEHAVIOR_LABELS[swapConfirming.slot.behavior] ?? swapConfirming.slot.behavior}</strong>.
              A carta que sai volta para a Coleção e seu progresso não muda.
            </p>
            <div className={styles.confirmActions}>
              <button
                className={styles.confirmBtn}
                onClick={handleSwapConfirm}
                disabled={isSwapping}
                aria-label="Confirmar alteração"
                type="button"
              >
                {isSwapping ? 'Alterando...' : 'Alterar'}
              </button>
              <button
                className={styles.cancelConfirmBtn}
                onClick={() => setSwapConfirming(null)}
                disabled={isSwapping}
                aria-label="Cancelar alteração"
                type="button"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
